import { useMemo, useState } from 'react';
import {
    Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, List,
    ListItem, ListItemButton, ListItemText, Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import { getWidget, ModuleKey, SELF_SERVICE, WIDGET_REGISTRY } from '../registry';
import { isGatedModule } from '../registry/moduleMeta';

export interface WidgetListValue {
    widgetIds: string[];
    lockedWidgetIds: string[];
}

interface Props {
    value: WidgetListValue;
    onChange: (next: WidgetListValue) => void;
    /** Modules the institution has enabled; the picker offers only widgets of these (and ungated ones). */
    enabledModules: ReadonlySet<ModuleKey>;
    disabled?: boolean;
}

/**
 * A board as a list of widget NAMES with add, remove, reorder and lock. It never renders a widget:
 * the editor sees the layout, not anyone's data. The permission key each widget needs is shown so
 * the editor knows a widget stays hidden for a person who lacks it (a layout never grants data).
 */
export default function WidgetListEditor({ value, onChange, enabledModules, disabled }: Props) {
    const [addOpen, setAddOpen] = useState(false);
    const { widgetIds, lockedWidgetIds } = value;

    const addable = useMemo(
        () => WIDGET_REGISTRY.filter(w =>
            !widgetIds.includes(w.id) && (!isGatedModule(w.module) || enabledModules.has(w.module))),
        [widgetIds, enabledModules],
    );

    const move = (from: number, to: number) => {
        if (to < 0 || to >= widgetIds.length) return;
        const next = [...widgetIds];
        const [m] = next.splice(from, 1);
        next.splice(to, 0, m);
        onChange({ widgetIds: next, lockedWidgetIds });
    };
    const toggleLock = (id: string) => {
        const next = lockedWidgetIds.includes(id) ? lockedWidgetIds.filter(x => x !== id) : [...lockedWidgetIds, id];
        onChange({ widgetIds, lockedWidgetIds: next });
    };
    const remove = (id: string) =>
        onChange({ widgetIds: widgetIds.filter(x => x !== id), lockedWidgetIds: lockedWidgetIds.filter(x => x !== id) });
    const add = (id: string) => onChange({ widgetIds: [...widgetIds, id], lockedWidgetIds });

    return (
        <Box data-testid="widget-list-editor" data-help-section="dashboard.editor">
            <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                    Widgets ({widgetIds.length}{lockedWidgetIds.length > 0 ? `, ${lockedWidgetIds.length} locked` : ''})
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button data-help="dashboard.editor.add" size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} disabled={disabled}>Add widget</Button>
            </Stack>

            {widgetIds.length === 0 ? (
                <Typography variant="body2" color="text.secondary" data-testid="widget-list-empty" data-help-section="dashboard.editor.empty">
                    No widgets. Everyone this applies to falls back to the next level (company default, then the built-in board).
                </Typography>
            ) : (
                <Paper variant="outlined">
                    <List dense disablePadding>
                        {widgetIds.map((id, i) => {
                            const def = getWidget(id);
                            const isLocked = lockedWidgetIds.includes(id);
                            const need = def && def.permission !== SELF_SERVICE ? def.permission : null;
                            return (
                                <ListItem key={id} divider={i < widgetIds.length - 1} data-testid={`widget-row-${id}`}
                                    secondaryAction={(
                                        <Stack direction="row" alignItems="center">
                                            <Tooltip title="Move up">
                                                <span><IconButton data-help="dashboard.editor.moveUp" size="small" aria-label={`Move ${def?.title ?? id} up`} disabled={disabled || i === 0} onClick={() => move(i, i - 1)}><ArrowUpwardIcon fontSize="small" /></IconButton></span>
                                            </Tooltip>
                                            <Tooltip title="Move down">
                                                <span><IconButton data-help="dashboard.editor.moveDown" size="small" aria-label={`Move ${def?.title ?? id} down`} disabled={disabled || i === widgetIds.length - 1} onClick={() => move(i, i + 1)}><ArrowDownwardIcon fontSize="small" /></IconButton></span>
                                            </Tooltip>
                                            <Tooltip title="The person cannot remove a locked widget">
                                                <FormControlLabel
                                                    data-help="dashboard.editor.lock"
                                                    sx={{ mr: 0.5, ml: 0.5 }}
                                                    control={<Checkbox size="small" checked={isLocked} disabled={disabled} onChange={() => toggleLock(id)}
                                                        inputProps={{ 'aria-label': `Lock ${def?.title ?? id}` }} icon={<LockIcon fontSize="small" color="disabled" />} checkedIcon={<LockIcon fontSize="small" color="primary" />} />}
                                                    label=""
                                                />
                                            </Tooltip>
                                            <IconButton data-help="dashboard.editor.remove" size="small" aria-label={`Remove ${def?.title ?? id}`} disabled={disabled} onClick={() => remove(id)}><CloseIcon fontSize="small" /></IconButton>
                                        </Stack>
                                    )}
                                    sx={{ pr: 22 }}
                                >
                                    <ListItemText
                                        data-help="dashboard.editor.row"
                                        primary={def?.title ?? `Unknown widget (${id})`}
                                        secondary={def ? `${def.module}${need ? ` · needs ${need}` : ''}` : 'Not in this version of the app; it stays on the board'}
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                </Paper>
            )}

            <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="xs" closeAfterTransition={false} data-help-page="dashboard.editor.addDialog.page">
                <DialogTitle>Add a widget</DialogTitle>
                <DialogContent dividers data-help-section="dashboard.editor.addDialog">
                    {addable.length === 0
                        ? <Typography variant="body2" color="text.secondary">Every widget of the enabled modules is already on the list.</Typography>
                        : (
                            <List>
                                {addable.map(w => (
                                    <ListItemButton key={w.id} data-help="dashboard.editor.addDialog.pick" onClick={() => { add(w.id); setAddOpen(false); }}>
                                        <ListItemText primary={w.title} secondary={`${w.module}${w.permission !== SELF_SERVICE ? ` · needs ${w.permission}` : ''}`} />
                                    </ListItemButton>
                                ))}
                            </List>
                        )}
                </DialogContent>
                <DialogActions><Button data-help="dashboard.editor.addDialog.close" onClick={() => setAddOpen(false)}>Close</Button></DialogActions>
            </Dialog>
        </Box>
    );
}
